import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * Single source of truth for invite deep links.
 *
 * Shared links use the HTTPS web URL ([WEB_INVITE_BASE]) so they render as tappable hyperlinks in
 * messaging apps. Android verifies this host with Digital Asset Links and iOS verifies it with the
 * Apple App Site Association file so installed devices can open invites directly in the app.
 */
object InviteLinks {
    private const val CODE_PARAM = "code"
    private const val INVITE_SOURCE_PARAM = "invite_source"

    /** HTTPS landing page that redirects into the app. Hosted on Firebase Hosting (project bankothegame). */
    private const val WEB_INVITE_BASE = "https://bankothegame.com/join"
    private const val WEB_HOME_BASE = "https://bankothegame.com/"

    /** Builds the shareable invite URL for a game code (HTTPS so it's tappable in messages). */
    fun buildInviteUrl(code: String): String =
        "$WEB_INVITE_BASE?$CODE_PARAM=$code&utm_source=banko_app&utm_medium=invite&utm_campaign=game_invite"

    /** Campaign-tagged destination used by recap shares so GA can separate them from direct visits. */
    fun buildRecapUrl(): String =
        "$WEB_HOME_BASE?utm_source=banko_app&utm_medium=share&utm_campaign=game_recap"

    /**
     * Pulls the game code out of an incoming invite URL, or null if it carries none.
     * Parses the query string directly so it is scheme-agnostic (works for custom scheme and HTTPS).
     */
    fun extractCode(url: String): String? {
        val query = url.substringAfter('?', missingDelimiterValue = "")
        if (query.isEmpty()) return null
        val code = query.split('&')
            .firstOrNull { it.substringBefore('=') == CODE_PARAM }
            ?.substringAfter('=', missingDelimiterValue = "")
            ?.uppercase()
            ?.filter { it.isLetterOrDigit() }
        return code?.takeIf { it.length == 5 }
    }

    private val _pendingInviteCode = MutableStateFlow<String?>(null)
    private var suggestedPlayerName: String? = null

    /** A code awaiting consumption by the start screen, set when the app opens via an invite link. */
    val pendingInviteCode: StateFlow<String?> = _pendingInviteCode.asStateFlow()

    /**
     * Supplies a nickname captured by the lightweight web invite page. The page stores the value in
     * same-origin sessionStorage instead of putting a person's name in the URL; the web entry point
     * transfers it here immediately before [onDeepLink].
     */
    fun setSuggestedPlayerName(name: String?) {
        suggestedPlayerName = name
            ?.trim()
            ?.takeIf { PlayerNameValidator.validate(it) is PlayerNameValidator.Result.Valid }
    }

    fun consumeSuggestedPlayerName(): String? = suggestedPlayerName.also {
        suggestedPlayerName = null
    }

    /** Called by the platform entry point when an invite URL is received. */
    fun onDeepLink(url: String) {
        extractCode(url)?.let {
            Analytics.inviteOpened(
                source = when {
                    url.contains("$INVITE_SOURCE_PARAM=web") -> "web_guest"
                    url.startsWith("banko://") -> "custom_scheme"
                    else -> "app_link"
                },
            )
            _pendingInviteCode.value = it
        }
    }

    /** Restores the room after a first Android install from a tagged Play Store invitation. */
    fun onInstallReferrer(referrer: String): Boolean {
        val code = extractCode("banko://join?$referrer") ?: return false
        Analytics.inviteOpened(source = "play_install_referrer")
        _pendingInviteCode.value = code
        return true
    }

    /** Clears the pending code once the start screen has applied it. */
    fun consume() {
        _pendingInviteCode.value = null
    }
}
